import {
  Alert,
  Box,
  Button,
  get,
  Icon,
  SkeletonLoader,
  Space,
  TextBody,
  useAccordionItem,
} from '@ally/metronome-ui'
import { muiPrinterOutline } from '@ally/metronome-icons'
import { Private } from '@ally/private'
import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTransactionDetailsApi } from '../../../../../api/transaction-detail-api'
import { CheckDetails, TransactionDetails } from '../../../../../types'
import {
  amountFormatter,
  getServerTime,
  getServerTimeShortDate,
} from '../../../../../utils/formatter'

const StyledSpan = styled.span`
  margin-right: ${get.space('xxs')};
`
const StyledImg = styled.img`
  width: 600px;
  margin: ${get.space('xxs')} 0 ${get.space('xs')};
`

const StyledButton = styled(Button)`
  display: none;
  ${get.media.up('md')} {
    display: block;
  }
`
// istanbul ignore next
export const CheckImageDrawerContent: FC<{
  transactionDetail: TransactionDetails
  printCheckCallback: (checkDetails: CheckDetails) => void
}> = ({ transactionDetail, printCheckCallback }) => {
  const [imagesLoading, setImagesLoading] = useState(true)
  const { fetchCheckImages } = useTransactionDetailsApi()
  const [images, setImages] = useState([])
  const [imagesError, setImagesError] = useState(false)
  const { checkImageUrl, transactionCheckNumberPvtEncrypt: checkNumber } =
    transactionDetail

  const { isExpanded } = useAccordionItem()
  // istanbul ignore next
  const getCheckImages = (): void => {
    // No need to test else case that does nothing
    /* istanbul ignore else */
    if (images.length === 0) {
      setImagesLoading(true)
      fetchCheckImages(checkImageUrl)
        .then(response => {
          setImages(response)
        })
        .catch(() => {
          setImagesError(true)
        })
        .finally(() => {
          setImagesLoading(false)
        })
    }
  }
  // istanbul ignore next
  const handlePrintCheck = (): void => {
    // We'll have to fix these eventually since capi-gw will be deprecated.
    // We have to talk to the api team and do further investigation on how to get images with the new apigee endpoint,
    // since they ask for some headers and tokens but we only get images with a url and we currently don't have logic to pass those.
    // They are also still returning old urls in fetch urls response and those would have to be updated.
    const printCheckDetails = {
      frontCheckImage: `/capi-gw${images[0]}`,
      backCheckImage: `/capi-gw${images[1]}`,
      type:
        transactionDetail.transactionAmountPvtEncrypt < 0
          ? 'Withdrawal'
          : 'Deposit',
      postingDate: getServerTimeShortDate(
        transactionDetail.transactionPostingDate,
      ),
      postingTime: getServerTime(transactionDetail.transactionPostingDate),
      amount: amountFormatter.format(
        transactionDetail.transactionAmountPvtEncrypt,
      ),
    }
    printCheckCallback(printCheckDetails)
  }

  useEffect(() => {
    if (isExpanded) {
      getCheckImages()
    }
  }, [isExpanded])

  return (
    <>
      {imagesError && (
        <Alert
          disableContainer
          variant="error"
          heading="We can't load your check images at this time. Try again later."
        />
      )}
      {imagesLoading && (
        <Box tabIndex={-1}>
          <SkeletonLoader.Container>
            <SkeletonLoader.Row height="l" width="s" />
            <SkeletonLoader.Row height="l" width="s" />
          </SkeletonLoader.Container>
        </Box>
      )}
      {!imagesLoading && !imagesError && (
        <Space mb="xs">
          <TextBody size="md" weight="bold" tag="p" tabIndex={-1}>
            Check Images
          </TextBody>
          <TextBody size="xs" weight="bold" tag="p">
            FRONT OF CHECK
          </TextBody>
          <Private>
            <StyledImg
              src={`/capi-gw${images[0]}`}
              alt={`front of check ${checkNumber}`}
              data-testid="frontCheckImage"
            />
          </Private>
          <TextBody size="xs" weight="bold" tag="p">
            BACK OF CHECK
          </TextBody>
          <Private>
            <StyledImg
              src={`/capi-gw${images[1]}`}
              alt={`back of check ${checkNumber}`}
              data-testid="backCheckImage"
            />
          </Private>
          <br />
          <StyledButton
            variant="link"
            id="printCheckImagesButton"
            data-testid="printCheckImagesButton"
            unsupportedContent={
              <>
                <StyledSpan>
                  <Icon aria-hidden="true" icon={muiPrinterOutline} size="sm" />
                </StyledSpan>
                {` `}
                Print check images
              </>
            }
            onClick={handlePrintCheck}
          />
        </Space>
      )}
    </>
  )
}
